import {
  inject,
  Injector,
  makeStateKey,
  ProviderToken,
  TransferState,
} from '@angular/core';
import { ResolveFn, RouterStateSnapshot } from '@angular/router';
import { AppService } from 'src/app/services/app.service';
import { PageModel } from 'src/app/models/page.model';

const pageCache = new Map<string, PageModel>();
const maxCachedPages = 50;

export const pageResolver: ResolveFn<PageModel> = async (
  _route,
  state: RouterStateSnapshot,
) => {
  const appService = inject(AppService);
  const transferState = inject(TransferState);
  const injector = inject(Injector);
  const statusCode = injector.get(
    'HTTP_STATUS' as unknown as ProviderToken<number>,
    null,
  );

  const requestedUrl = buildPageApiUrl(state.url);

  let apiUrl = requestedUrl;

  if (statusCode === 404) {
    apiUrl = 'page?url=/404';
  }

  const stateKey = getTransferStateKey(apiUrl);
  const requestedStateKey = getTransferStateKey(requestedUrl);

  if (!appService.isServer() && pageCache.has(apiUrl)) {
    return pageCache.get(apiUrl) ?? new PageModel();
  }

  if (!appService.isServer() && transferState.hasKey(requestedStateKey)) {
    const page = transferState.get(requestedStateKey, new PageModel());
    transferState.remove(requestedStateKey);
    cachePage(apiUrl, page, appService.isServer());
    return page;
  }

  if (!appService.isServer() && transferState.hasKey(stateKey)) {
    const page = transferState.get(stateKey, new PageModel());
    transferState.remove(stateKey);
    cachePage(apiUrl, page, appService.isServer());
    return page;
  }

  const response = await appService.get(apiUrl, { transferCache: true });
  const page = JSON.parse(response) as PageModel;

  cachePage(apiUrl, page, appService.isServer());

  if (appService.isServer()) {
    transferState.set(stateKey, page);

    if (requestedUrl !== apiUrl) {
      transferState.set(requestedStateKey, page);
    }
  }

  return page;
};

function getTransferStateKey(url: string) {
  return makeStateKey<PageModel>(`page-data:${url}`);
}

function cachePage(url: string, page: PageModel, isServer: boolean): void {
  if (isServer) return;

  if (pageCache.size >= maxCachedPages) {
    const oldestKey = pageCache.keys().next().value;

    if (oldestKey) {
      pageCache.delete(oldestKey);
    }
  }

  pageCache.set(url, page);
}

function buildPageApiUrl(routerUrl: string): string {
  const url = routerUrl.split('?')[0].split('#')[0];
  const pathSegments = url.split('/').filter(Boolean);

  if (
    pathSegments.length >= 2 &&
    pathSegments[0].toLowerCase() === 'pagetemplate'
  ) {
    const id = encodeURIComponent(pathSegments[1]);
    return `PageTemplate/${id}`;
  }

  if (
    pathSegments.length >= 2 &&
    pathSegments[0].toLowerCase() === 'pagebyid'
  ) {
    const id = encodeURIComponent(pathSegments[1]);
    return `Page/ById/${id}`;
  }

  return `page?url=${url}`;
}
